import { FullStory, init as initFS } from '@fullstory/browser';
import { fullStoryIntegration } from '@sentry/fullstory';
import * as Sentry from '@sentry/react';
import type { Integration } from '@sentry/types';
import * as Spotlight from '@spotlightjs/spotlight';
import config from 'config';
import { isPublicRevision } from 'factories/revision';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { readCookieRevision } from 'redux/selectors/global';
import debug from 'utils/debug';

let initialized = false;

function initializeSentry() {
  const isPreviewingRevision = !isPublicRevision(readCookieRevision() || 'current');
  const isDevelopment = __DEVELOPMENT__;

  if (initialized) {
    return;
  }

  const baseUrl = config.baseUrl;
  const environmentAccordingToUrl =
    !!baseUrl && baseUrl.includes('timelyapp.com') ? 'production' : 'staging';

  const environment = isDevelopment
    ? 'development'
    : isPreviewingRevision
      ? 'preview'
      : environmentAccordingToUrl;

  const release = window.ASSETS_REVISION
    ? window.ASSETS_REVISION.substring(0, 7)
    : `unversioned-${environment}`;

  // In local dev, we don't want to send any data to Sentry,
  // but we want it initialized so we can use Spotlight
  const SENTRY_DSN = isDevelopment
    ? undefined
    : 'https://ce767d679a624586991c5bbd1b04aac1@sentry.io/114853';

  /** Fraction between 0-1 of sessions that will be sent to Sentry perfomance metrics. */
  const sampleRate = environment === 'production' ? 0.005 : 1;

  const DEBUG_DUMP = { SENTRY_DSN, environment, release, sampleRate };
  // Old value was 'timely-as', but this seems incorrect ref https://app.fullstory.com/ui/143RB/settings/general
  // Before changing org id, two requests were going out...
  // 1. https://rs.fullstory.com/s/settings/timely-as/v1/web (202)
  // 2.https://edge.fullstory.com/s/settings/timely-as/v1/web (404)
  // Is Segment involved on #1? Weird.
  initFS({ orgId: '143RB' });
  Sentry.init({
    dsn: SENTRY_DSN,
    tunnel: isDevelopment ? undefined : 'https://ep.timely-cdn.com/tunnel/114853/',
    environment,
    // Releases to Sentry via CircleCI are named $CIRCLE_SHA1 (7 char hash), but Redis returns 8 chars
    // Root cause is code in qa-dashboard-api & timely-redis
    release,
    ignoreErrors: [
      // Failure due to user's connection
      /TypeError: Failed to fetch/i,
      /TypeError: NetworkError when attempting to fetch resource/i,
      /Loading (?:.*)?chunk (?:\d+) failed/i,
    ],
    denyUrls: [
      /static\.hotjar\.com/i, // Hotjar blocked
      // /cdn\.segment\.com/i, // Segment, see ref https://github.com/Timely/Frontend/pull/1309#discussion_r295445860
      /www\.google-analytics\.com/i, // Google Analytics blocked
      /js\.intercomcdn\.com/i, // Intercom blocked
      /graph\.facebook\.com/i, // Facebook flakiness
      /connect\.facebook\.net\/en_US\/all\.js/i, // Facebook blocked
      /eatdifferent\.com\.woopra-ns\.com/i, // Woopra flakiness
      /static\.woopra\.com\/js\/woopra\.js/i, // Woopra flakiness
      /extensions\//i, // Chrome extensions
      /^chrome:\/\//i, // Chrome extensions
      /^resource:\/\//i, // Firefox extensions
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
    // Filter non-actionable errors to reduce noise and cost
    beforeSend: (event, hint) => {
      const error = hint.originalException as Error | undefined;
      const message = error?.message || event.message || '';

      // ResizeObserver loop errors are benign browser quirks
      if (message.includes('ResizeObserver')) {
        return null;
      }

      // AbortError is intentional request cancellation
      if (error?.name === 'AbortError') {
        return null;
      }

      // QuotaExceededError when localStorage is full - not actionable
      if (error?.name === 'QuotaExceededError') {
        return null;
      }

      // Script errors from cross-origin scripts we can't debug
      if (message === 'Script error.' && !event.exception?.values?.[0]?.stacktrace) {
        return null;
      }

      return event;
    },
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Basic replacement of numbers & hashes in URLs
        beforeStartSpan: context => {
          // For some reason, the pageload event still prefixes the account ID, while navigation events don't?
          const name = location.pathname
            .replace(/\/[a-f0-9]{32}/g, '/<hash>')
            .replace(/\/\d+/g, '/*');

          return {
            ...context,
            name,
          };
        },
      }),
      Sentry.reportingObserverIntegration({ types: ['crash', 'deprecation', 'intervention'] }),
      fullStoryIntegration('timely-as', { client: FullStory }) as Integration,
    ],
    tracePropagationTargets: [
      'localhost:8080',
      'localhost:3011',
      'https://integrations.app.timelyapp.com/api',
      'https://integrations.app.betimely.com/api',
    ],
    tracesSampleRate: sampleRate,
    profilesSampleRate: sampleRate,
  });

  if (isDevelopment) {
    checkSpotlightService().then(isSpotlightRunning => {
      if (isSpotlightRunning) {
        Spotlight.init({ anchor: 'centerRight' });
        debug.template.sentry(
          `Local-only initialized for ${environment} using Spotlight (http://localhost:8969)`,
          DEBUG_DUMP
        );
      } else {
        debug.template.sentry(
          `Spotlight service not available at http://localhost:8969 - skipping initialization`,
          DEBUG_DUMP
        );
      }
    });
  } else {
    debug.template.sentry(`Initialized for ${environment}`, DEBUG_DUMP);
  }
  initialized = true;
}
const checkSpotlightService = async () => {
  try {
    const response = await fetch('http://localhost:8969/', {
      method: 'HEAD',
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

initializeSentry();
